//adapted from https://github.com/mblarsen/browser-acl

import { hasScope } from "@/utils/jwt";

export interface Permission {
  resource: string
  action: string
}

export interface Grant {
  role: {
    companies: unknown[]
    is_master: boolean
    type: string
    permissions: Permission[]
  }
}

export interface AclTenant {
  id: number
  grants: Grant[]
}

export interface AclUser {
  tenants: AclTenant[]
}

export interface AclTenantRef {
  id: number
}

type GrantInfo = Map<string, unknown>

export default class Acl {
  rules: Map<number, GrantInfo[]>;

  constructor() {
    this.rules = new Map();
  }

  can(user: AclUser | null, tenant: AclTenantRef | null, resource: string, action: string): boolean {
    if (!tenant) return false;
    const grants = this.rules.get(tenant.id);
    if (!grants) return false;

    for (const grant of grants) {
      if (grant.get("is_master")) {
        return true;
      }
      const permissions = grant.get("permissions") as Map<string, Map<string, boolean>> | undefined;
      if (!permissions) continue;

      const actions = permissions.get(resource);

      if (actions && actions.get(action)) {
        return true;
      }
    }

    return false;
  }

  hasScope(token: string, requiredScope: string): boolean {
    return hasScope(token, requiredScope);
  }

  reset(user: AclUser | null | undefined): this {
    if (!user) {
      this.rules = new Map();
      return this;
    }
    for (const tenant of user.tenants) {
      const tenantInfo: GrantInfo[] = [];
      for (const grant of tenant.grants) {
        const grantInfo: GrantInfo = new Map();
        grantInfo.set("companies", grant.role.companies);
        grantInfo.set("is_master", grant.role.is_master);
        grantInfo.set("role_type", grant.role.type);

        const permInfo = new Map<string, Map<string, boolean>>();
        for (const permission of grant.role.permissions) {
          const resourceInfo = permInfo.get(permission.resource) || new Map<string, boolean>();
          resourceInfo.set(permission.action, true);
          permInfo.set(permission.resource, resourceInfo);
        }
        grantInfo.set("permissions", permInfo);
        tenantInfo.push(grantInfo);
      }
      this.rules.set(tenant.id, tenantInfo);
    }

    return this;
  }
}
